import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Card, Button, Space, Typography } from 'antd'
import { MenuOutlined } from '@ant-design/icons'

import { PackLine } from './Packs'
import { ItemLine } from './Items'
import { defaultPacks } from './defaultValues'
import {
    buildUnifiedSelections,
    getNextDisplayOrder,
    applyDisplayOrders,
} from './selectionUtils'

const { Text } = Typography

const DraggableRow = ({
    disabled,
    dragIndex,
    onDragStart,
    onDrop,
    children,
}) => {
    const [isOver, setIsOver] = useState(false)

    return (
        <div
            onDragOver={(e) => {
                if (disabled) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                setIsOver(true)
            }}
            onDragLeave={() => setIsOver(false)}
            onDrop={(e) => {
                if (disabled) return
                e.preventDefault()
                setIsOver(false)
                onDrop(dragIndex)
            }}
            style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                borderTop: isOver
                    ? '2px dashed #1890ff'
                    : '2px solid transparent',
            }}
        >
            {!disabled && (
                <div
                    draggable
                    onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move'
                        onDragStart(dragIndex)
                    }}
                    onDragEnd={() => setIsOver(false)}
                    style={{ marginTop: 16, cursor: 'grab' }}
                >
                    <MenuOutlined style={{ color: '#999' }} />
                </div>
            )}
            <div style={{ flex: 1 }}>{children}</div>
        </div>
    )
}

const OfferSelections = ({
    control,
    channel,
    isEdit,
    errors,
    setValue,
    clearErrors,
    type,
}) => {
    const { getValues, reset, watch } = useFormContext()
    const packs = watch(`${channel}.packs`) || []
    const items = watch(`${channel}.items`) || []
    const unified = buildUnifiedSelections(packs, items)
    const [dragIndex, setDragIndex] = useState(null)

    const onRemovePack = (index) => {
        const values = getValues()
        values[channel].packs.splice(index, 1)
        reset(values, { isDirty: true })
    }

    const onRemoveItem = (index) => {
        const values = getValues()
        values[channel].items.splice(index, 1)
        reset(values, { isDirty: true })
    }

    const addPack = () => {
        clearErrors(`${channel}.packs.size`)
        const values = getValues()
        const currentPacks = values[channel].packs || []
        const currentItems = values[channel].items || []
        const displayOrder = getNextDisplayOrder(currentPacks, currentItems)
        values[channel].packs = [
            ...currentPacks,
            { ...defaultPacks, displayOrder },
        ]
        reset(values, { isDirty: true })
    }

    const addItem = () => {
        clearErrors(`${channel}.packs.size`)
        const values = getValues()
        const currentPacks = values[channel].packs || []
        const currentItems = values[channel].items || []
        const displayOrder = getNextDisplayOrder(currentPacks, currentItems)
        values[channel].items = [...currentItems, { displayOrder }]
        reset(values, { isDirty: true })
    }

    const handleDrop = (dropIndex) => {
        if (dragIndex === null || dragIndex === dropIndex) {
            setDragIndex(null)
            return
        }

        const reordered = [...unified]
        const [moved] = reordered.splice(dragIndex, 1)
        reordered.splice(dropIndex, 0, moved)

        const values = getValues()
        const { packs: newPacks, items: newItems } = applyDisplayOrders(
            values[channel].packs || [],
            values[channel].items || [],
            reordered
        )
        values[channel].packs = newPacks
        values[channel].items = newItems
        reset(values, { isDirty: true })
        setDragIndex(null)
    }

    return (
        <Card
            bordered={false}
            title="Selections"
            bodyStyle={{ paddingTop: 12 }}
        >
            {unified.map((entry, index) => (
                <DraggableRow
                    key={entry.key}
                    dragIndex={index}
                    disabled={isEdit}
                    onDragStart={setDragIndex}
                    onDrop={handleDrop}
                >
                    {entry.type === 'pack' ? (
                        <PackLine
                            type={type}
                            packIndex={entry.arrayIndex}
                            selectionLabel={entry.letter}
                            control={control}
                            channel={channel}
                            isEdit={isEdit}
                            errors={errors?.packs?.[entry.arrayIndex]}
                            setValue={setValue}
                            clearErrors={clearErrors}
                            onRemove={() => onRemovePack(entry.arrayIndex)}
                        />
                    ) : (
                        <ItemLine
                            type={type}
                            itemIndex={entry.arrayIndex}
                            selectionLabel={entry.letter}
                            control={control}
                            channel={channel}
                            isEdit={isEdit}
                            setValue={setValue}
                            errors={errors?.items?.[entry.arrayIndex]}
                            onRemove={() => onRemoveItem(entry.arrayIndex)}
                        />
                    )}
                </DraggableRow>
            ))}

            <Space style={{ marginTop: 12 }}>
                <Button type="primary" onClick={addPack} disabled={isEdit}>
                    Add pack
                </Button>
                <Button type="primary" onClick={addItem} disabled={isEdit}>
                    Add single item
                </Button>
            </Space>

            {!!errors?.size && (
                <div style={{ marginTop: 12 }}>
                    <Text type="danger">{errors?.size?.message}</Text>
                </div>
            )}
        </Card>
    )
}

export default OfferSelections
